import cn from 'classnames';
import {
  ColumnFiltersState,
  ExpandedState,
  PaginationState,
  SortingState,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { NotAvailable } from '~/components/_common/Images/NotAvailable';
import { ScreenEmptyImage } from '~/components/_common/Images/ScreenEmptyImage';
import { Spinner } from '~/components/_tclOverrides/Spinner/Spinner';
import { useTranslation } from '~/i18n';

import { useTableStyles } from './tableBaseStyles';
import FilterableDataTableBody from './FilterableDataTableBody';
import FilterableDataTableHead from './FilterableDataTableHead';
import FilterableDataTablePagination from './FilterableDataTablePagination';
import useCustomStyles from './FilterableDataTable.styles';
import { tableColumnsToColumnDefs } from './tableColumnDefs';
import type {
  ExtendRowPropsFn,
  FilterableDataTableClasses,
  FilterableDataTableFilter,
  FilterableDataTableProps,
} from './tableTypes';

export type { FilterableDataTableColumnDefinition } from './tableColumnDefs';
export type {
  FilterableDataTableClasses,
  FilterableDataTableFilter,
  FilterableDataTableProps,
  FilterableDataTableSort,
} from './tableTypes';
export type { ExtendCellPropsFn, ExtendRowPropsFn, CellClickHandler } from './tableTypes';

export const DEFAULT_TABLE_PAGE_SIZE = 15;

const noop = () => {};
const passthroughDomProps: ExtendRowPropsFn = (props) => props;

function TableEmptyState() {
  const t = useTranslation();
  return <NotAvailable description={t('tables.chrome.noResults')} Image={ScreenEmptyImage} />;
}

function mergeClassKey(
  boronClasses: ReturnType<typeof useTableStyles>,
  customClasses: ReturnType<typeof useCustomStyles>,
  externalClasses: FilterableDataTableClasses,
  key: keyof FilterableDataTableClasses,
): string {
  const k = key as string;
  return cn(
    (boronClasses as Record<string, string | undefined>)[k],
    (customClasses as Record<string, string | undefined>)[k],
    externalClasses[key],
  );
}

/** Width / padding rules shared by head and body cells. */
function setCellWidthAndPadding(cellProps: { style?: React.CSSProperties }, width: string | number | undefined): void {
  const nextStyle = { ...cellProps.style };
  if (width != null && width !== '') {
    nextStyle.width = width;
    nextStyle.maxWidth = width;
    nextStyle.minWidth = 0;
  }
  cellProps.style = nextStyle;
}

function columnWidthFromMeta(meta: unknown): string | number | undefined {
  if (meta == null || typeof meta !== 'object') {
    return undefined;
  }
  return (meta as { columnWidth?: string | number }).columnWidth;
}

function defaultGetRowId<D extends object>(row: D, index: number): string {
  return (row as { id?: string }).id ?? String(index);
}

export function FilterableDataTable<D extends object>({
  data,
  columns,
  getRowHref,
  getRowLinkLabel,
  onCellClick,
  onRowLinkClick,
  renderExpandedRow,
  expandedRowIsSubtable,
  classes: externalClasses = {},
  pageNumber: controlledPageIndex,
  pageCount: controlledPageCount,
  pageSize: controlledPageSize,
  onPageChange = noop,
  onPageSizeChange,
  onFiltersChange = noop,
  sortBy: controlledSortBy,
  initialSortBy,
  onSortChange,
  isLoading = false,
  extendRowProps = passthroughDomProps,
  showExpandedRowByDefault = false,
  extendCellProps = passthroughDomProps,
  defaultColumn,
  tableName,
}: FilterableDataTableProps<D>) {
  const boronClasses = useTableStyles();
  const customClasses = useCustomStyles();
  const isServerPaginated = typeof controlledPageCount === 'number';

  /** Skip the initial mount so parents do not get a spurious `onFiltersChange([])`. */
  const skipFirstFilterEmit = useRef(true);
  const skipFirstSortEmit = useRef(true);
  const onFiltersChangeRef = useRef(onFiltersChange);
  onFiltersChangeRef.current = onFiltersChange;

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [sorting, setSorting] = useState<SortingState>(() => initialSortBy ?? []);
  const [pagination, setPagination] = useState<PaginationState>(() => ({
    pageIndex: controlledPageIndex ?? 0,
    pageSize: controlledPageSize ?? DEFAULT_TABLE_PAGE_SIZE,
  }));

  const classNameFor = useCallback(
    (key: keyof FilterableDataTableClasses) => mergeClassKey(boronClasses, customClasses, externalClasses, key),
    [boronClasses, customClasses, externalClasses],
  );

  const tanstackColumns = useMemo(() => tableColumnsToColumnDefs(columns, defaultColumn), [columns, defaultColumn]);

  const table = useReactTable({
    data,
    columns: tanstackColumns,
    state: { pagination, columnFilters, expanded, sorting },
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onExpandedChange: setExpanded,
    onSortingChange: setSorting,
    manualPagination: isServerPaginated,
    manualFiltering: isServerPaginated,
    manualSorting: isServerPaginated,
    pageCount: isServerPaginated ? controlledPageCount : undefined,
    autoResetPageIndex: !isServerPaginated,
    defaultColumn: { minSize: 0, size: 0 },
    meta: { tableName },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: !isServerPaginated ? getExpandedRowModel() : undefined,
    getFilteredRowModel: !isServerPaginated ? getFilteredRowModel() : undefined,
    getPaginationRowModel: !isServerPaginated ? getPaginationRowModel() : undefined,
    getSortedRowModel: !isServerPaginated ? getSortedRowModel() : undefined,
    getRowCanExpand: () => Boolean(renderExpandedRow),
    getRowId: defaultGetRowId,
  });

  const pageRows = isServerPaginated ? table.getCoreRowModel().rows : table.getPaginationRowModel().rows;
  const showEmptyState = !isLoading && pageRows.length === 0;
  const tableClassName = cn(classNameFor('table'), {
    [classNameFor('clickableCells')]: Boolean(onCellClick || renderExpandedRow),
    [classNameFor('subtableParent')]: expandedRowIsSubtable,
  });

  const handlePageChange = useCallback(
    (page: number) => {
      if (isServerPaginated) {
        onPageChange(page);
      } else {
        table.setPageIndex(page - 1);
      }
    },
    [isServerPaginated, onPageChange, table],
  );

  useEffect(() => {
    if (controlledPageIndex === undefined) {
      return;
    }
    setPagination((prev) =>
      prev.pageIndex === controlledPageIndex ? prev : { ...prev, pageIndex: controlledPageIndex },
    );
  }, [controlledPageIndex]);

  useEffect(() => {
    if (!controlledPageSize) {
      return;
    }
    setPagination((prev) => (prev.pageSize === controlledPageSize ? prev : { ...prev, pageSize: controlledPageSize }));
  }, [controlledPageSize]);

  useEffect(() => {
    if (isServerPaginated) {
      table.toggleAllRowsExpanded(false);
    }
  }, [isServerPaginated, pagination.pageIndex, pagination.pageSize, table]);

  useEffect(() => {
    if (controlledSortBy === undefined) {
      return;
    }
    setSorting(controlledSortBy);
  }, [controlledSortBy]);

  const filtersSignature = JSON.stringify(columnFilters);
  useEffect(() => {
    if (skipFirstFilterEmit.current) {
      skipFirstFilterEmit.current = false;
      return;
    }
    onFiltersChangeRef.current(columnFilters as FilterableDataTableFilter[]);
  }, [filtersSignature]);

  const sortSignature = JSON.stringify(sorting);
  useEffect(() => {
    if (!isServerPaginated || onSortChange === undefined) {
      return;
    }
    if (skipFirstSortEmit.current) {
      skipFirstSortEmit.current = false;
      return;
    }
    onSortChange(sorting);
  }, [isServerPaginated, onSortChange, sortSignature]);

  const leafColumns = table.getVisibleLeafColumns();

  return (
    <div className={classNameFor('wrapper')}>
      <table className={tableClassName}>
        <colgroup>
          {leafColumns.map((column) => {
            const width = columnWidthFromMeta(column.columnDef.meta);
            return (
              <col
                key={column.id}
                style={width != null && width !== '' ? { width, maxWidth: width, minWidth: 0 } : { minWidth: 0 }}
              />
            );
          })}
        </colgroup>
        <FilterableDataTableHead
          classNameFor={classNameFor}
          setCellWidthAndPadding={setCellWidthAndPadding}
          table={table}
        />
        <FilterableDataTableBody
          classNameFor={classNameFor}
          extendCellProps={extendCellProps}
          extendRowProps={extendRowProps}
          getRowHref={getRowHref}
          getRowLinkLabel={getRowLinkLabel}
          onCellClick={onCellClick}
          onRowLinkClick={onRowLinkClick}
          pageRows={pageRows}
          renderExpandedRow={renderExpandedRow}
          setCellWidthAndPadding={setCellWidthAndPadding}
          showExpandedRowByDefault={showExpandedRowByDefault}
        />
      </table>
      {isLoading && (
        <Spinner
          loading
          centered
          className={cn({
            [classNameFor('spinnerHeight')]: data.length <= 1,
          })}
        />
      )}
      {showEmptyState && (
        <div className={classNameFor('noContentWrapper')}>
          <TableEmptyState />
        </div>
      )}
      <FilterableDataTablePagination
        table={table}
        hidePagination={!table.getPageCount()}
        manualPagination={isServerPaginated}
        onPageSizeChange={onPageSizeChange}
        onPageChange={handlePageChange}
      />
    </div>
  );
}
